import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Smartphone, Send, Loader2 } from 'lucide-react';
import { Card } from './AdminPanelPrimitives';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// At-a-glance per-platform user counts + a one-click "Notify iPhone users —
// App Store version is live" broadcast preset. Sits at the top of the
// Broadcasts tab so iOS waitlist activation is a single click on launch day.
export const DevicePlatformWaitlistCard = ({ headers }) => {
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/device-platforms/counts`, { headers });
      setCounts(res.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load device counts');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const notifyIosUsers = async () => {
    setSending(true);
    try {
      const payload = {
        title: 'Allure is now on the App Store',
        message:
          "Thanks for waiting! The Allure iPhone app is live on the App Store. Tap to install — your account is ready and waiting for you.",
        channels: ['push', 'in_app', 'email'],
        audience: { type: 'device_platform', device_platform: 'ios' },
      };
      const res = await axios.post(`${API}/admin/broadcasts`, payload, { headers });
      toast.success(`Sent to ${res.data?.recipient_count || 0} iPhone user(s).`);
      setConfirmOpen(false);
      fetchCounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const iosCount = counts?.ios ?? 0;
  const androidCount = counts?.android ?? 0;
  const desktopCount = counts?.desktop ?? 0;
  const totalActive = counts?.total_active_users ?? 0;

  return (
    <Card style={{ padding: 18 }} data-testid="admin-device-waitlist-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <Smartphone size={18} color="#111" />
        <h3 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 18 }}>
          Device platforms &amp; iOS waitlist
        </h3>
      </div>
      <p style={{ margin: '4px 0 12px', fontSize: 12.5, color: '#666' }}>
        Tracks the device each user has signed in from. Use this to broadcast the App Store
        launch directly to iPhone users the moment it goes live.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        <CountTile label="iPhone (iOS)" value={iosCount} total={totalActive} tone="ios" testId="admin-device-count-ios" loading={loading} />
        <CountTile label="Android" value={androidCount} total={totalActive} tone="android" testId="admin-device-count-android" loading={loading} />
        <CountTile label="Desktop" value={desktopCount} total={totalActive} tone="desktop" testId="admin-device-count-desktop" loading={loading} />
      </div>

      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={iosCount === 0 || sending}
          data-testid="admin-device-notify-ios-button"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
            borderRadius: 12, border: 'none', cursor: (iosCount === 0 || sending) ? 'not-allowed' : 'pointer',
            fontWeight: 700, fontSize: 13,
            background: iosCount === 0 ? '#9aa0a6' : '#0F1320', color: '#FFF',
          }}
        >
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Notify iPhone users — App Store live
        </button>
        <span style={{ fontSize: 12, color: '#666' }}>
          Sends push + in-app + email to all {iosCount.toLocaleString()} iPhone user{iosCount === 1 ? '' : 's'}.
        </span>
      </div>

      {confirmOpen ? (
        <ConfirmModal
          iosCount={iosCount}
          sending={sending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={notifyIosUsers}
        />
      ) : null}
    </Card>
  );
};

function CountTile({ label, value, total, tone, testId, loading }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const accent = tone === 'ios' ? '#0F1320' : tone === 'android' ? '#3DDC84' : '#5B7EFF';
  return (
    <div
      data-testid={testId}
      style={{
        padding: 12,
        borderRadius: 12,
        background: '#F7F8FB',
        border: '1px solid #E7E9EE',
      }}
    >
      <p style={{ margin: 0, fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
        {label}
      </p>
      <p style={{ margin: '6px 0 2px', fontSize: 22, fontWeight: 700, color: '#111' }}>
        {loading ? '—' : value.toLocaleString()}
      </p>
      <p style={{ margin: 0, fontSize: 11, color: accent, fontWeight: 700 }}>
        {loading ? '\u00a0' : `${pct}% of users`}
      </p>
    </div>
  );
}

function ConfirmModal({ iosCount, sending, onCancel, onConfirm }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="admin-device-notify-confirm-modal"
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(10,8,22,0.55)',
        backdropFilter: 'blur(6px)', zIndex: 10600, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFF', borderRadius: 18, maxWidth: 440, width: '100%',
          padding: '22px 22px 18px', boxShadow: '0 24px 60px rgba(0,0,0,0.30)',
        }}
      >
        <h3 style={{ margin: '0 0 8px', fontFamily: "'Inria Serif', serif", fontSize: 20 }}>
          Notify {iosCount.toLocaleString()} iPhone user{iosCount === 1 ? '' : 's'}?
        </h3>
        <p style={{ margin: '0 0 14px', fontSize: 13.5, color: '#555', lineHeight: 1.5 }}>
          We&apos;ll send <strong>push + in-app + email</strong> to every user who has signed in from an
          iPhone. Use this only when the App Store version is actually live.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={sending}
            data-testid="admin-device-notify-cancel"
            style={{ padding: '9px 14px', borderRadius: 10, background: '#F4F5F8', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={sending}
            data-testid="admin-device-notify-confirm"
            style={{
              padding: '9px 16px', borderRadius: 10, border: 'none', cursor: sending ? 'wait' : 'pointer',
              fontWeight: 700, fontSize: 13, background: '#DD0608', color: '#FFF',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send now
          </button>
        </div>
      </div>
    </div>
  );
}

export default DevicePlatformWaitlistCard;
